import i18next from 'i18next';
import { generateId } from '../../../shared/lib/id';
import type { Room } from '@/entities/room/model/types';
import type { EstimateItem } from '@/shared/model';
import type { Service } from '@/entities/service/model/types';

/**
 * Creates a deep copy of a room with new IDs for the room itself and all its items.
 */
export const cloneRoom = (roomToClone: Room): Room | null => {
    if (!roomToClone) return null;

    const newId = generateId();
    
    // Deep clone basic structure
    const clonedRoom: Room = JSON.parse(JSON.stringify(roomToClone));
    
    // Update Identity
    clonedRoom.id = newId;
    const copySuffix = i18next.t('common.copy_suffix', ' (копия)');
    clonedRoom.name = roomToClone.name ? `${roomToClone.name}${copySuffix}` : '';
    
    // Regenerate IDs for items to avoid conflicts
    if (Array.isArray(clonedRoom.items)) {
        clonedRoom.items = clonedRoom.items.map(item => ({
            ...item,
            id: generateId()
        }));
    }

    return clonedRoom;
};

/**
 * Builds the payload for copying an existing item into another room, preserving the
 * source's typed adjustment verbatim (TRUTH-04: no numeric fallback that could produce NaN).
 */
export const cloneEstimateItemForRoom = (source: EstimateItem): EstimateItem => ({
    id: generateId(),
    serviceId: source.serviceId,
    name: source.name,
    nameKey: source.nameKey,
    unit: source.unit,
    unitType: source.unitType,
    categoryId: source.categoryId,
    category: source.category,
    categoryName: source.categoryName,
    priceLabor: source.priceLabor,
    qty: 0,
    userQty: source.userQty,
    total: 0,
    isManual: source.isManual,
    priceIsManual: source.priceIsManual,
    adjustment: source.adjustment,
});

/**
 * Prepares service items to be added to a room.
 */
export const createRoomItemsFromServices = (servicesToAdd: Service[]): EstimateItem[] => {
    if (!Array.isArray(servicesToAdd)) return [];

    return servicesToAdd.map(service => ({
        id: generateId(),
        serviceId: service.id,
        name: service.name,
        nameKey: service.nameKey,
        categoryId: service.categoryId,
        category: service.categoryId, // legacy fallback
        unit: service.unit,
        unitType: service.unitType || service.unit,
        priceLabor: service.priceLabor,
        qty: 0,
        userQty: 0,
        total: 0,
        isManual: false,
        priceIsManual: false,
        adjustment: { op: 'mul' as const, value: 1 }
    }));
};
